import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import * as nodesSelectors from 'data/nodes/nodes.selectors';
import * as nodesActions from 'data/nodes/nodes.actions';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import XdpForm from 'routes/Xdp/components/XdpForm';
import { Ref } from 'routes/Xdp/components/XdpForm/XdpForm';
import { RootState } from 'data/root.reducers';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { adaptToCreate, adaptToServerFormat } from 'data/xdp/xdp.adapter';
import * as xdpActions from 'data/xdp/xdp.actions';
import { Xdp } from 'data/xdp/xdp.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { newXdp } from 'routes/Xdp/Xdp.types';
import { xdpTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptFormData,
  adaptXdpTemplate,
  adaptTemplateOptions,
} from '../../utils/xdpTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/xdpTemplateDataToSubmit';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  xdpTemplateDef: adaptTemplateVisibilityMode(
    xdpTemplate,
    'create',
  ),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  nodes: nodesSelectors.getNodes(state),
});

const mapDispatch = {
  createXdp: xdpActions.createXdp,
  goToXdpList: xdpActions.goToXdpList,
  getXdps: xdpActions.getXdps,
  getNodes: nodesActions.getNodes,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const XdpCreate = ({
  history,
  userId,
  deliveryFunctionId,
  createXdp,
  userName,
  isAuditEnabled,
  xdpTemplateDef,
  goToXdpList,
  getNodes,
  nodes,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Xdp>(
    cloneDeep(newXdp),
  );
  const [template, setTemplate] = useState(xdpTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    getNodes(deliveryFunctionId);
  }, [deliveryFunctionId, getNodes]);

  useEffect(() => {
    const adaptedTemplate = adaptTemplateOptions(xdpTemplateDef, undefined, nodes);
    setTemplate(adaptedTemplate);
  }, [xdpTemplateDef, nodes]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as Xdp);
    let clonedTemplate = cloneDeep(template);

    let newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptXdpTemplate(
      clonedTemplate,
    );

    newData = adaptFormData(newData);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateXdpActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const xdpFormData = adaptToServerFormat(formRef.current.getValues());
    const payload = {
      data: xdpFormData,
      auditDetails: getAuditDetailsToSubmit(
        xdpFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    return payload;
  };

  const handleClickSave = () => {
    const payload = getCreateXdpActionPayload();
    if (payload) {
      createXdp(deliveryFunctionId, payload, history);
    }
  };

  const handleCancel = () => goToXdpList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <XdpForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="XdpCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('xdp.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(XdpCreate));
